import React, { useState, useEffect, useCallback } from 'react';
import TimePicker from 'react-time-picker';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import multiMonthPlugin from '@fullcalendar/multimonth';
import 'react-time-picker/dist/TimePicker.css';
import 'react-clock/dist/Clock.css';
import { PlayFab } from '../services/PlayFab';
import './SessionsPage.css';
import { EventContentArg } from '@fullcalendar/core';
import CanvasJSReact from '@canvasjs/react-charts'; // Import CanvasJS
const CanvasJSChart = CanvasJSReact.CanvasJSChart;

// Keep Session interface (add optional id if not already there from cloud script)
interface Session {
  id: string; // Added by cloud script
  date: string;
  location: string;
  stakes: string;
  moneyIn: number;
  moneyOut: number;
  length: string; 
  timestamp: string; // Added by cloud script
}

// Helper function to calculate duration
function calculateDuration(start: string, end: string): string {
  if (!start || !end) return '0m';

  try {
    const [startHours, startMinutes] = start.split(':').map(Number);
    const [endHours, endMinutes] = end.split(':').map(Number);

    let startTotalMinutes = startHours * 60 + startMinutes;
    let endTotalMinutes = endHours * 60 + endMinutes;

    // Handle crossing midnight
    if (endTotalMinutes < startTotalMinutes) {
      endTotalMinutes += 24 * 60; // Add a day's worth of minutes
    }

    const diffMinutes = endTotalMinutes - startTotalMinutes;

    if (diffMinutes <= 0) return '0m'; // Or handle invalid input

    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    let durationString = '';
    if (hours > 0) {
      durationString += `${hours}h`;
    }
    if (minutes > 0) {
      durationString += `${durationString ? ' ' : ''}${minutes}m`;
    }
    return durationString || '0m'; // Return "0m" if duration is exactly 0
  } catch (error) {
    console.error("Error calculating duration:", error);
    return 'Error'; // Indicate an error in calculation
  }
}

// Define Value type based on react-time-picker documentation (can be string | null)
type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];
type TimePickerValue = string | null;

// Define Tab types
type ActiveTab = 'new' | 'history' | 'stats';

// Helper to parse session length string (e.g., "2h 30m") into total hours
function parseLengthToHours(lengthStr: string): number {
    if (!lengthStr) return 0;
    let totalHours = 0;
    const hourMatch = lengthStr.match(/(\d+)\s*h/);
    const minMatch = lengthStr.match(/(\d+)\s*m/);
    if (hourMatch) {
        totalHours += parseInt(hourMatch[1], 10);
    }
    if (minMatch) {
        totalHours += parseInt(minMatch[1], 10) / 60;
    }
    return totalHours;
}

function SessionsPage() {
  // Form State
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]); // Default to today
  const [location, setLocation] = useState('');
  const [stakes, setStakes] = useState('');
  const [moneyIn, setMoneyIn] = useState<number | string>('');
  const [moneyOut, setMoneyOut] = useState<number | string>('');
  const [startTime, setStartTime] = useState<TimePickerValue>('19:00'); // Default to 7 PM
  const [endTime, setEndTime] = useState<TimePickerValue>('22:00');   // Default to 10 PM
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false); // Add saving state

  // Tab and History State
  const [activeTab, setActiveTab] = useState<ActiveTab>('new');
  const [sessions, setSessions] = useState<Session[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState('');

  // Delete Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [sessionToDeleteId, setSessionToDeleteId] = useState<string | null>(null);

  // Stats State
  const [statsYear, setStatsYear] = useState<number>(new Date().getFullYear());

  // --- Fetch Session History --- 
  const fetchSessions = useCallback(async () => {
    if (!PlayFab.hasValidSession()) {
        // Maybe prompt login or show message if needed?
        console.log("Not logged in, cannot fetch sessions.");
        return;
    }
    setIsLoadingHistory(true);
    setHistoryError('');
    try {
      console.log("Fetching PokerSessions data...");
      const result = await PlayFab.getUserTitleData(['PokerSessions']);
      console.log("Fetch result:", result);
      
      // Safely access the Value property
      const pokerSessionsValue = result.data?.PokerSessions?.Value;

      // Check if the fetch was successful AND we got the value string
      if (result.success && pokerSessionsValue) { 
        const fetchedSessions: Session[] = JSON.parse(pokerSessionsValue); 
        if (Array.isArray(fetchedSessions)) {
          // Sort sessions by date descending (most recent first)
          fetchedSessions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
          setSessions(fetchedSessions);
        } else {
          console.warn("PokerSessions data is not an array:", fetchedSessions);
          setSessions([]);
        }
      } else {
        console.log("No PokerSessions data found or fetch failed.");
        setSessions([]); // Clear sessions if no data
      }
    } catch (err: any) {
      console.error("Error fetching sessions:", err);
      setHistoryError("Failed to load session history.");
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    // Fetch history when the component mounts
    fetchSessions();
  }, [fetchSessions]);

  // --- Handle Save Session --- 
  const handleSaveSession = async () => {
    setMessage('');
    setError('');
    setIsSaving(true); // Set saving state

    const moneyInNum = parseFloat(moneyIn as string);
    const moneyOutNum = parseFloat(moneyOut as string);
    
    // Ensure startTime and endTime are strings before calculating
    const startString = typeof startTime === 'string' ? startTime : '';
    const endString = typeof endTime === 'string' ? endTime : '';
    const calculatedLength = calculateDuration(startString, endString);

    // Validation check
    if (!date || !location || !stakes || isNaN(moneyInNum) || isNaN(moneyOutNum) || !startString || !endString || calculatedLength === 'Error') {
      setError('Please fill in all fields correctly, including valid start and end times.');
      setIsSaving(false);
      return;
    }

    const sessionData = {
      date,
      location,
      stakes,
      moneyIn: moneyInNum,
      moneyOut: moneyOutNum,
      length: calculatedLength, // Use the calculated duration
    };

    setMessage('Saving session...');
    console.log("Calling logPokerSession with data:", sessionData);

    try {
      const result = await PlayFab.executeCloudScript('logPokerSession', sessionData);
      console.log("Cloud Script result:", result);

      // Access the deeply nested FunctionResult
      const functionResultPayload = result.data?.FunctionResult?.FunctionResult;

      if (functionResultPayload?.success) {
        setMessage(functionResultPayload.message || 'Session saved successfully!');
        setError('');
        // Refresh history after successful save
        await fetchSessions(); 
      } else {
        // Check for error message within the nested payload or fallback
        setError(functionResultPayload?.error || result.data?.FunctionResult?.error || result.data?.errorMessage || 'Failed to save session.'); 
        setMessage('');
      }
    } catch (err: any) {
      console.error("Error calling Cloud Script:", err);
      const errorMessage = err?.message || err?.errorMessage || 'An error occurred while saving the session.';
      setError(errorMessage);
      setMessage('');
    } finally {
        setIsSaving(false); // Reset saving state
    }
  };

  // --- Modified Delete Session Handler (Opens Modal) ---
  const handleDeleteSession = (sessionId: string) => {
    console.log(`Request to delete session: ${sessionId}`);
    setSessionToDeleteId(sessionId);
    setIsDeleteModalOpen(true);
  };

  // --- New Handler for Confirming Deletion --- 
  const confirmDeleteSession = async () => {
    if (!sessionToDeleteId) return; // Should not happen if modal is open

    console.log(`Confirming delete for session: ${sessionToDeleteId}`);
    setIsDeleteModalOpen(false); // Close modal immediately
    setMessage('Deleting session...');
    setError('');

    try {
      const result = await PlayFab.executeCloudScript('deletePokerSession', { sessionId: sessionToDeleteId });
      console.log("Delete Cloud Script result:", result);

      const functionResultPayload = result.data?.FunctionResult?.FunctionResult;

      if (functionResultPayload?.success) {
        setMessage(functionResultPayload.message || 'Session deleted successfully!');
        setError('');
        await fetchSessions(); // Refresh history
      } else {
        setError(functionResultPayload?.error || result.data?.FunctionResult?.error || result.data?.errorMessage || 'Failed to delete session.'); 
        setMessage('');
      }
    } catch (err: any) {
      console.error("Error calling delete Cloud Script:", err);
      const errorMessage = err?.message || err?.errorMessage || 'An error occurred while deleting the session.';
      setError(errorMessage);
      setMessage('');
    } finally {
       setSessionToDeleteId(null); // Clear the ID
       setTimeout(() => setMessage(''), 3000);
    }
  };

  const cancelDeleteSession = () => {
    setIsDeleteModalOpen(false);
    setSessionToDeleteId(null);
  };

  // --- Calculate Stats --- 
  const calculateStats = (year: number) => {
      const yearSessions = sessions
          .filter(s => new Date(s.date).getFullYear() === year)
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()); // Sort chronological for chart/streaks

      if (yearSessions.length === 0) {
          return { 
              hourlyRate: 0, 
              longestWinningStreak: 0, 
              longestLosingStreak: 0, 
              chartData: [],
              totalProfit: 0,
              totalHours: 0
          };
      }

      let totalProfit = 0;
      let totalHours = 0;
      let currentWinStreak = 0;
      let longestWinStreak = 0;
      let currentLoseStreak = 0;
      let longestLoseStreak = 0;
      let cumulativeProfit = 0;
      const chartDataPoints = [{ x: new Date(year, 0, 1), y: 0 }]; // Start year at 0 profit

      yearSessions.forEach((session) => {
          const profit = session.moneyOut - session.moneyIn;
          const hours = parseLengthToHours(session.length);
          
          totalProfit += profit;
          totalHours += hours;
          cumulativeProfit += profit;

          // Add data point for chart
          chartDataPoints.push({
              x: new Date(session.date + 'T12:00:00'), // Use mid-day to avoid timezone issues
              y: cumulativeProfit
          });

          // Calculate streaks
          if (profit > 0) {
              currentWinStreak++;
              currentLoseStreak = 0;
              longestWinStreak = Math.max(longestWinStreak, currentWinStreak);
          } else if (profit < 0) {
              currentLoseStreak++;
              currentWinStreak = 0;
              longestLoseStreak = Math.max(longestLoseStreak, currentLoseStreak);
          } else {
              // Neutral session resets both streaks
              currentWinStreak = 0;
              currentLoseStreak = 0;
          }
      });

      const hourlyRate = totalHours > 0 ? totalProfit / totalHours : 0;

      return {
          hourlyRate: hourlyRate,
          longestWinningStreak: longestWinStreak,
          longestLosingStreak: longestLoseStreak,
          chartData: chartDataPoints,
          totalProfit: totalProfit,
          totalHours: totalHours
      };
  };

  // --- Render Functions --- 

  const renderNewSessionForm = () => (
    <div className="session-form">
      <div className="form-group">
        <label htmlFor="session-date">Date:</label>
        <input
          id="session-date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          disabled={isSaving} // Disable during save
        />
      </div>
      <div className="form-group">
        <label htmlFor="session-location">Location:</label>
        <input
          id="session-location"
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="e.g., Home Game, Casino XYZ"
          disabled={isSaving} // Disable during save
        />
      </div>
      <div className="form-group">
        <label htmlFor="session-stakes">Stakes:</label>
        <input
          id="session-stakes"
          type="text"
          value={stakes}
          onChange={(e) => setStakes(e.target.value)}
          placeholder="e.g., $1/$2 NLH, $5 Tournament"
          disabled={isSaving} // Disable during save
        />
      </div>
      <div className="form-group">
        <label htmlFor="session-money-in">Money In ($):</label>
        <input
          id="session-money-in"
          type="number"
          value={moneyIn}
          onChange={(e) => setMoneyIn(e.target.value)}
          placeholder="e.g., 100"
          disabled={isSaving} // Disable during save
        />
      </div>
      <div className="form-group">
        <label htmlFor="session-money-out">Money Out ($):</label>
        <input
          id="session-money-out"
          type="number"
          value={moneyOut}
          onChange={(e) => setMoneyOut(e.target.value)}
          placeholder="e.g., 150"
          disabled={isSaving} // Disable during save
        />
      </div>

      {/* Start and End Time Pickers */}
      <div className="form-group form-group-time">
          <label htmlFor="session-start-time">Start Time:</label>
          <TimePicker 
              id="session-start-time"
              onChange={setStartTime} 
              value={startTime}
              disableClock={true} // Using text input mode
              format="HH:mm" // 24-hour format
              clearIcon={null} // Hide clear icon
              className="time-picker-input" // Add class for styling
              disabled={isSaving} // Disable during save
          />
      </div>
      <div className="form-group form-group-time">
          <label htmlFor="session-end-time">End Time:</label>
          <TimePicker 
              id="session-end-time"
              onChange={setEndTime} 
              value={endTime} 
              disableClock={true} // Using text input mode
              format="HH:mm" // 24-hour format
              clearIcon={null} // Hide clear icon
              className="time-picker-input" // Add class for styling
              disabled={isSaving} // Disable during save
          />
      </div>
      
      <button onClick={handleSaveSession} disabled={isSaving}> 
        {isSaving ? 'Saving...' : 'Save Session'}
      </button>
      <div className="message-area"> 
        {message && <p className="success-message">{message}</p>}
        {error && <p className="error-message">{error}</p>}
      </div>
    </div>
  );

  // Custom Event Content Renderer
  const renderEventContent = (eventInfo: EventContentArg) => {
    const eventId = eventInfo.event.id;
    return (
      <>
        <span className="fc-event-title-custom">{eventInfo.event.title}</span>
        <div className="session-event-delete-container">
          <button 
              className="session-event-delete-btn" 
              onClick={(e) => { 
                  e.stopPropagation(); 
                  handleDeleteSession(eventId); 
              }}
              title="Delete Session"
          >
              X
          </button>
        </div>
      </>
    );
  }

  const renderSessionHistory = () => {
    const events = sessions.map(session => {
      const result = session.moneyOut - session.moneyIn;
      const resultPrefix = result >= 0 ? '+' : '';
      const title = `${resultPrefix}${result.toFixed(0)}${session.length ? ' (' + session.length + ')' : ''}`; 
      const className = result >= 0 ? 'fc-event-positive' : 'fc-event-negative';
      return {
        id: session.id,
        title: title, // Title is still needed for tooltip/accessibility
        start: session.date, 
        allDay: true,
        className: className,
        extendedProps: { 
            location: session.location,
            stakes: session.stakes,
            moneyIn: session.moneyIn,
            moneyOut: session.moneyOut,
            length: session.length
        }
      };
    });

    return (
      <div className="session-history">
        {isLoadingHistory && <p>Loading history...</p>}
        {historyError && <p className="error-message">{historyError}</p>}
        {!isLoadingHistory && !historyError && (
          <div className="fullcalendar-container">
            <FullCalendar
              plugins={[dayGridPlugin, multiMonthPlugin]} 
              initialView="dayGridMonth" 
              events={events} 
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,dayGridWeek,multiMonthYear' 
              }}
              multiMonthMaxColumns={3} 
              eventContent={renderEventContent} // Use custom renderer
              // eventClick={...} // Keep commented out or remove if not needed
            />
          </div>
        )}
        {/* Show delete feedback message area */} 
        <div className="message-area action-feedback"> 
            {message && <p className="success-message">{message}</p>}
            {error && <p className="error-message">{error}</p>}
        </div>
      </div>
    );
  };

  // --- Updated Stats Tab Rendering --- 
  const renderStats = () => {
      // Get unique years from sessions for the selector
      const availableYears = Array.from(new Set(sessions.map(s => new Date(s.date).getFullYear()))).sort((a, b) => b - a);
      if (availableYears.length === 0 && sessions.length > 0) {
          // Handle edge case where sessions might exist but year parsing failed somehow?
          // Or just default to current year if no sessions yet
          availableYears.push(new Date().getFullYear());
      }

      const stats = calculateStats(statsYear);

      const chartOptions = {
          animationEnabled: true,
          theme: "dark2", // Use CanvasJS dark theme
          backgroundColor: "#172a45", // Match button bg or choose another dark shade
          title: {
              text: `Cumulative Profit - ${statsYear}`,
              fontColor: "#ccd6f6",
              fontSize: 18
          },
          axisX: {
              valueFormatString: "MMM DD",
              labelFontColor: "#8892b0",
              lineColor: "#444c5a",
              tickColor: "#444c5a"
          },
          axisY: {
              title: "Cumulative Profit ($)",
              prefix: "$",
              titleFontColor: "#ccd6f6",
              labelFontColor: "#8892b0",
              gridColor: "#1d2d44",
              lineColor: "#444c5a",
              tickColor: "#444c5a"
          },
          toolTip: {
            shared: true,
            contentFormatter: (e: any) => {
              let content = `<strong>${CanvasJSReact.CanvasJS.formatDate(e.entries[0].dataPoint.x, "MMM DD, YYYY")}</strong><br/>`;
              content += `Cumulative Profit: $${e.entries[0].dataPoint.y.toFixed(2)}`;
              return content;
            }
          },
          data: [{
              type: "line",
              xValueFormatString: "MMM DD, YYYY",
              yValueFormatString: "$#,##0.00",
              color: "#5691f5", // Use active button blue for line
              dataPoints: stats.chartData
          }]
      }

      return (
          <div className="stats-view">
              <h3>Statistics for {statsYear}</h3>
              {/* Year Selector */} 
              <div className="year-selector">
                  <label htmlFor="stats-year">Select Year:</label>
                  <select 
                      id="stats-year"
                      value={statsYear}
                      onChange={(e) => setStatsYear(parseInt(e.target.value, 10))}
                      disabled={availableYears.length <= 1} >
                      {availableYears.map(year => (
                          <option key={year} value={year}>{year}</option>
                      ))}
                  </select>
              </div>

              {sessions.length === 0 || stats.chartData.length <= 1 ? (
                  <p className="no-stats-message">Not enough session data for {statsYear} to display stats.</p>
              ) : (
                  <>
                      <div className="stats-summary">
                          <div className="stat-item">
                              <span className="stat-label">Total Profit/Loss:</span>
                              <span className={`stat-value ${stats.totalProfit >= 0 ? 'positive-result' : 'negative-result'}`}>
                                  {stats.totalProfit >= 0 ? '+' : ''}{stats.totalProfit.toFixed(2)}$
                              </span>
                          </div>
                          <div className="stat-item">
                              <span className="stat-label">Total Hours:</span>
                              <span className="stat-value">{stats.totalHours.toFixed(1)}h</span>
                          </div>
                           <div className="stat-item">
                              <span className="stat-label">Hourly Rate:</span>
                              <span className={`stat-value ${stats.hourlyRate >= 0 ? 'positive-result' : 'negative-result'}`}>
                                  {stats.hourlyRate >= 0 ? '+' : ''}{stats.hourlyRate.toFixed(2)}$/h
                              </span>
                          </div>
                          <div className="stat-item">
                              <span className="stat-label">Longest Win Streak:</span>
                              <span className="stat-value">{stats.longestWinningStreak} sessions</span>
                          </div>
                          <div className="stat-item">
                              <span className="stat-label">Longest Lose Streak:</span>
                              <span className="stat-value">{stats.longestLosingStreak} sessions</span>
                          </div>
                      </div>

                      <div className="stats-chart-container">
                          <CanvasJSChart options={chartOptions} />
                      </div>
                  </>
              )}
          </div>
      );
  }

  // --- Render Modal --- 
  const renderDeleteConfirmationModal = () => {
      if (!isDeleteModalOpen) return null;

      return (
          <div className="modal-overlay">
              <div className="modal-content">
                  <h4>Confirm Deletion</h4>
                  <p>Are you sure you want to delete this session?</p>
                  <div className="modal-actions">
                      <button onClick={cancelDeleteSession} className="button-secondary">Cancel</button>
                      <button onClick={confirmDeleteSession} className="button-danger">Delete Session</button>
                  </div>
              </div>
          </div>
      );
  }

  return (
    <div className="sessions-page">
      <div className="tabs">
        <button 
          className={`tab-button ${activeTab === 'new' ? 'active' : ''}`}
          onClick={() => setActiveTab('new')}
        >
          New Session
        </button>
        <button 
          className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          History
        </button>
        <button 
          className={`tab-button ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          Stats
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'new' && renderNewSessionForm()}
        {activeTab === 'history' && renderSessionHistory()}
        {activeTab === 'stats' && renderStats()}
      </div>
      {renderDeleteConfirmationModal()}
    </div>
  );
}

export default SessionsPage; 